import {
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type Ref,
  cloneElement,
  isValidElement,
  useMemo,
  useState,
} from "react";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClientPoint,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import "@/components/UI/Tooltip.css";

interface TooltipProps {
  children: ReactElement<Record<string, unknown>>;
  className?: string;
  content: ReactNode;
  delay?: number;
  followCursor?: boolean;
  placement?: "top" | "right" | "bottom" | "left" | "bottom-start";
}

export function Tooltip({
  children,
  className,
  content,
  delay = 500,
  followCursor = false,
  placement = "top",
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const {
    context,
    floatingStyles,
    refs,
  } = useFloating({
    middleware: [
      offset(followCursor
        ? { mainAxis: 14, crossAxis: 14 }
        : 8),
      flip(),
      shift({ padding: 12 }),
    ],
    onOpenChange: setOpen,
    open,
    placement,
    whileElementsMounted: autoUpdate,
  });
  const hover = useHover(context, {
    delay: { open: delay, close: 0 },
    move: followCursor,
  });
  const focus = useFocus(context);
  const role = useRole(context, { role: "tooltip" });
  const clientPoint = useClientPoint(context, {
    enabled: followCursor,
  });
  const {
    getFloatingProps,
    getReferenceProps,
  } = useInteractions([
    hover,
    focus,
    role,
    clientPoint,
  ]);
  const childRef = getElementRef(children);
  const referenceProps = useMemo(() => (
    getReferenceProps({
      ...children.props,
      ref: mergeRefs<Element>(refs.setReference, childRef),
    })
  ), [childRef, children.props, getReferenceProps, refs.setReference]);

  if (!isValidElement(children)) return null;

  return (
    <>
      {cloneElement(children, referenceProps)}
      {open ? (
        <FloatingPortal>
          <div
            className={["tooltip", className].filter(Boolean).join(" ")}
            ref={refs.setFloating}
            style={{
              ...(floatingStyles as CSSProperties),
              zIndex: "var(--z-tooltip)",
            }}
            {...getFloatingProps()}
          >
            {content}
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}

function getElementRef(
  element: ReactElement<Record<string, unknown>>,
): Ref<Element> | undefined {
  return element.props.ref as Ref<Element> | undefined;
}

function mergeRefs<T>(
  ...refs: Array<Ref<T> | undefined>
): Ref<T> {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return;

      if (typeof ref === "function") {
        ref(node);
        return;
      }

      (ref as { current: T | null }).current = node;
    });
  };
}
